import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { isPortfolioCompleted } from '@/utils/portfolioStatus';
import { PortfolioTablePagination } from '@/components/portfolio/table/PortfolioTablePagination';
import { PortfolioTableHeader } from '@/components/portfolio/table/PortfolioTableHeader';
import { usePortfolioTableState } from '@/hooks/portfolio/usePortfolioTableState';
import { PortfolioTableRow } from '@/components/portfolio/table/PortfolioTableRow';
import { computeClassTotals } from '@/components/portfolio/table/utils';

export interface AllocationForRow {
  id: string;
  portfolio_id: string;
  asset_id: string;
  asset_type: string;
  allocation_percentage: number;
  allocation_value?: number | null;
  is_value_locked?: boolean | null;
  currency_code?: string | null;
  fund?: { asset_class?: string; sa_asset_class?: string; fund_name?: string };
}
 
function properCaseLabel(input?: string | null): string {
  if (!input) return '—';
  return String(input)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\w/g, (m) => m.toUpperCase());
}

function computeIsCompletedByPercentage(p: EnhancedPortfolioRow): boolean {
  const allocs = p.portfolio_allocations || [];
  if (!allocs.length) return false;
  const totalPct = allocs.reduce((sum, a) => sum + (typeof a.allocation_percentage === 'number' ? a.allocation_percentage : Number(a.allocation_percentage) || 0), 0);
  const hasAny = allocs.some(a => (a.allocation_percentage || 0) > 0);
  // Match card behavior (essentially 100%). Allow tiny FP tolerance.
  return hasAny && Math.abs(totalPct - 100) < 0.0001;
}

// Narrow row type compatible with data prepared in PortfolioList
export interface EnhancedPortfolioRow {
  id: string;
  name: string;
  status: string;
  portfolio_size?: number;
  portfolio_type?: string;
  portfolio_allocations?: AllocationForRow[];
}

interface BulkSelectionLite {
  isSelected: (id: string) => boolean;
  toggleItem: (id: string) => void;
  toggleAll: (allIds: string[]) => void;
}

interface PortfolioTableProps {
  portfolios: EnhancedPortfolioRow[];
  bulkSelection: BulkSelectionLite;
  onRowClick: (portfolioId: string) => void;
}

function getPortfolioCurrency(portfolioId: string): string | undefined {
  try {
    const lsKey = `portfolio:currency:${portfolioId}`;
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(lsKey) : null;
    if (v && v.trim()) return v.trim().toUpperCase();
  } catch {}
  return undefined;
}

// computeClassTotals moved to '@/components/portfolio/table/utils'

export const PortfolioTable: React.FC<PortfolioTableProps> = ({ portfolios, bulkSelection, onRowClick }) => {
  type TableSortField = 'name' | 'type' | 'total';

  // Horizontal scroll hint state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getScrollTarget = useCallback((): HTMLDivElement | null => {
    // Use INNER shadcn wrapper as the sole horizontal scroller
    const outer = scrollRef.current;
    if (!outer) return null;
    const inner = outer.querySelector('div.overflow-auto') as HTMLDivElement | null;
    return inner || outer;
  }, []);
  const {
    pageData,
    totalCount,
    totalPages,
    currentPage,
    rowsPerPage,
    pageInputValue,
    handlePageChange,
    handlePageInputChange,
    handlePageInputBlur,
    handleRowsPerPageChange,
    sortField,
    sortDir,
    handleSortClick,
    headerChecked,
  } = usePortfolioTableState<EnhancedPortfolioRow>(portfolios, bulkSelection, (t) => properCaseLabel(t));

  // Update scroll hint visibility
  useEffect(() => {
    const el = getScrollTarget();
    if (!el) return;
    // Inner wrapper handles horizontal scrolling only
    try {
      el.style.overflowX = 'scroll';
      el.style.overflowY = 'hidden';
      el.style.maxWidth = '100%';
      el.style.setProperty('-webkit-overflow-scrolling', 'touch');
      // Reserve space for scrollbar so it doesn't overlay content (Chromium)
      try { (el.style as any).scrollbarGutter = 'stable both-edges'; } catch {}
      el.style.paddingBottom = '4px';
    } catch {}
    let rafId: number | null = null;
    const updateNow = () => {
      try {
        const { scrollLeft, clientWidth, scrollWidth } = el;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      } catch {}
    };
    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateNow();
      });
    };
    updateNow();
    el.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', scheduleUpdate as EventListener);
      window.removeEventListener('resize', scheduleUpdate as EventListener);
    };
  }, [pageData, rowsPerPage, currentPage, getScrollTarget]);

  const renderSortIcon = useCallback((field: TableSortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
  }, [sortField, sortDir]);

  return (
    <div className="w-full mr-auto max-w-[960px] space-y-4">
      <div className="rounded-md border relative overflow-hidden">
        <div
          className="w-full max-w-[100vw] overflow-hidden relative"
          ref={scrollRef}
        >
          
          <div className="w-full">
            <Table className="w-max min-w-[960px] table-auto text-[13px]">
            <TableHeader className="bg-muted/60 sticky top-0 border-b z-10">
              <PortfolioTableHeader
                headerChecked={headerChecked}
                onToggleAll={() => bulkSelection.toggleAll(pageData.map(p => p.id))}
                onSort={(field) => handleSortClick(field)}
                renderSortIcon={(field) => renderSortIcon(field)}
              />
            </TableHeader>
            <TableBody className="[&_tr]:border-0">
              <AnimatePresence key={currentPage}>
                {pageData.map((portfolio, index) => {
                  const currency = getPortfolioCurrency(portfolio.id) || undefined;
                  const totals = computeClassTotals(portfolio);
                  const completed = isPortfolioCompleted(portfolio as any);
                  const typeLabel = properCaseLabel(portfolio.portfolio_type);
                  return (
                    <PortfolioTableRow
                      key={portfolio.id}
                      portfolio={portfolio}
                      index={index}
                      currency={currency}
                      totals={totals}
                      completed={completed}
                      typeLabel={typeLabel}
                      isSelected={bulkSelection.isSelected(portfolio.id)}
                      onToggleSelected={() => bulkSelection.toggleItem(portfolio.id)}
                      onRowClick={() => onRowClick(portfolio.id)}
                    />
                  );
                })}
              </AnimatePresence>
              {pageData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-6 text-muted-foreground">
                    No portfolios found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </div>
      </div>
      {/* Footer pagination controls */}
      {totalCount > 0 && (
        <PortfolioTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageInputValue={pageInputValue}
          onPageChange={handlePageChange}
          onPageInputChange={handlePageInputChange}
          onPageInputBlur={handlePageInputBlur}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};
